/* eslint-disable @typescript-eslint/ban-ts-comment */
import { ArgumentError } from '@visiba/std';

type Disposer = () => void;

enum FlowState {
	ACCESSIBLE,
	UNSET,
	PENDING,
	ERROR,
}

type CurrStateFn<T> = (currState: T) => T;

type DispatchedTulip<T> = [Readonly<T>, FlowState];

interface IState<T> {
	flowState: FlowState;
	inFlow(action: FlowState): void;
	subscribe(event: () => void): Disposer;
	reduce(state: T, action: FlowState): void;
	mutate(fn: CurrStateFn<T>): void;
	read(): DispatchedTulip<T>;
}

let ids = 0;

export class State<T> implements IState<T> {
	public flowState = FlowState.UNSET;
	private hooks: Map<string, () => void> = new Map();

	constructor(
		private data: T,
		designatedFlowState?: FlowState,
	) {
		if (typeof data === 'object') {
			// @ts-ignore
			this.emptyState = {};
		} else {
			throw new ArgumentError('a state can only be represented as an object literal');
		}

		if (designatedFlowState !== undefined) this.flowState = designatedFlowState;
	}

	public read(): DispatchedTulip<T> {
		// The following is purposely being ignored by TypeScript, when the state is
		// not accessible we want to prevent any reading from.

		switch (this.flowState) {
			case FlowState.PENDING:
				// @ts-ignore
				return [undefined, FlowState.PENDING];
			case FlowState.UNSET:
				// @ts-ignore
				return [undefined, FlowState.UNSET];
			case FlowState.ERROR:
				return [this.data, FlowState.ERROR];
			default:
				return [this.data, FlowState.ACCESSIBLE];
		}
	}

	public unsafeRead(): T {
		return this.data;
	}

	public inFlow(changeFlowTo: FlowState): void {
		this.flowState = changeFlowTo;

		this.dispatch();
	}

	public mutate(currState: CurrStateFn<T>, changeFlowTo?: FlowState | null): void {
		try {
			const newState = currState(this.data);

			this.data = newState;
			if (changeFlowTo !== null) this.flowState = changeFlowTo || FlowState.ACCESSIBLE;

			this.dispatch();
		} catch (error) {
			console.error('could not mutate the state');
		}
	}

	public reduce(_: T, action: FlowState): void {
		switch (action) {
			case FlowState.ACCESSIBLE:
				break;
			case FlowState.PENDING:
				break;
			case FlowState.ERROR:
				break;
			default:
				throw new ArgumentError('noop action type');
		}
	}

	public subscribe(event: () => void): Disposer {
		const id = `hook_id_${++ids}`;
		this.hooks.set(id, event);

		return () => {
			this.hooks.delete(id);
		};
	}

	private dispatch(): void {
		this.hooks.forEach((hook) => hook());
	}
}

// eslint-disable-next-line @typescript-eslint/ban-types
function newApplicationState<T extends object>(state: T, designatedFlowState?: FlowState): State<T> {
	return new State(state, designatedFlowState);
}

export { newApplicationState, IState, FlowState, Disposer, DispatchedTulip };
